const DEFAULT_MAX_BATCH_BYTES = 128 * 1024

const getUtf8ByteLength = value => {
  let bytes = 0
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index)
    if (code <= 0x7f) {
      bytes += 1
    } else if (code <= 0x7ff) {
      bytes += 2
    } else if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
      bytes += 4
      index += 1
    } else {
      bytes += 3
    }
  }
  return bytes
}

const splitEventsByByteSize = (events, maxBatchBytes = DEFAULT_MAX_BATCH_BYTES) => {
  const batches = []
  let batch = []
  let batchBytes = 2

  events.forEach(event => {
    const eventBytes = getUtf8ByteLength(JSON.stringify(event) || 'null')
    const separatorBytes = batch.length ? 1 : 0
    // 后端单批有体积上限，这里按 UTF-8 字节数切分，避免中文事件被字符数低估。
    if (batch.length && batchBytes + separatorBytes + eventBytes > maxBatchBytes) {
      batches.push(batch)
      batch = []
      batchBytes = 2
    }
    batch.push(event)
    batchBytes += (batch.length > 1 ? 1 : 0) + eventBytes
  })

  if (batch.length) batches.push(batch)
  return batches
}

const hashString = value => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16)
}

const createBatchId = (events, startTime) => {
  const json = JSON.stringify(events)
  const firstTimestamp = events[0] && events[0].timestamp
  const lastTimestamp = events[events.length - 1] && events[events.length - 1].timestamp
  // 批次 ID 由时间范围、事件数、字节数和内容 hash 组成，支持服务端幂等去重。
  return [
    'v1',
    startTime || '',
    firstTimestamp || '',
    lastTimestamp || '',
    events.length,
    getUtf8ByteLength(json),
    hashString(json)
  ].join(':')
}

const createEventUploadQueue = ({
  events,
  persist,
  send,
  onError = () => {},
  maxBatchBytes = DEFAULT_MAX_BATCH_BYTES
}) => {
  let inFlight = null
  let activeBatches = []
  let nextBatchIndex = 0

  const persistEvents = () => {
    try {
      // 只持久化尚未确认的批次和新采集事件，页面刷新后可以从失败点续传。
      const unconfirmedEvents = []
      for (let index = nextBatchIndex; index < activeBatches.length; index++) {
        unconfirmedEvents.push(...activeBatches[index])
      }
      unconfirmedEvents.push(...events)
      persist(unconfirmedEvents)
    } catch (_) {}
  }

  const append = event => {
    events.push(event)
    persistEvents()
  }

  const upload = metadata => {
    if (inFlight) return inFlight
    if (!events.length) return Promise.resolve(true)

    const snapshot = events.splice(0, events.length)
    const batches = splitEventsByByteSize(snapshot, maxBatchBytes)
    activeBatches = batches
    nextBatchIndex = 0
    persistEvents()

    inFlight = (async () => {
      let batchIndex = 0
      try {
        for (; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex]
          await send({
            events: batch,
            batchId: createBatchId(batch, metadata.startTime),
            metadata
          })
          nextBatchIndex = batchIndex + 1
          persistEvents()
        }
        return true
      } catch (error) {
        // 上传中断时把未确认批次放回队首，保留事件顺序并等待下一次重试。
        const pendingEvents = []
        for (let index = batchIndex; index < activeBatches.length; index++) {
          pendingEvents.push(...activeBatches[index])
        }
        activeBatches = []
        nextBatchIndex = 0
        events.unshift(...pendingEvents)
        persistEvents()
        onError(error)
        return false
      } finally {
        activeBatches = []
        nextBatchIndex = 0
        inFlight = null
      }
    })()

    return inFlight
  }

  const drain = async metadata => {
    let isUploaded = true
    // drain 用于页面切换/销毁前清空队列，新事件可能在上一轮上传期间继续追加。
    do {
      isUploaded = await upload(metadata)
    } while (isUploaded && events.length)
    return isUploaded
  }

  return {
    append,
    drain,
    upload,
    isUploading: () => Boolean(inFlight)
  }
}

module.exports = {
  DEFAULT_MAX_BATCH_BYTES,
  createBatchId,
  createEventUploadQueue,
  getUtf8ByteLength,
  splitEventsByByteSize
}
